import React, { useState } from 'react';
import { panelPost } from '../../kit';
import {
  ConfirmDialog, Overview, SECTION, STATE_UNKNOWN, Trigger, capText, closedGates, num, windowText,
} from './shared';

/**
 * The per-trigger switch (On + mode) and the words its confirm uses.
 *
 * Switching OFF and back to Manual happen at once — they only ever stop
 * mail. Switching ON, or choosing Auto, always opens a confirm that says
 * exactly what will start happening given the other gates as they are now;
 * a switch that would let members get mail without anyone pressing Send
 * also needs a ticked acknowledgement. Everything goes through
 * email/memac_trigger_toggle, which the server audits.
 */

/** What the trigger sends, in words. */
export const contentText = (t: Trigger): string => {
  if (t.content_mode === 'template') {
    if (!t.template_key) return 'no template picked yet';
    if (t.template_library) {
      return `the saved template “${t.template_name || t.template_key}”${t.template_version ? ` (v${t.template_version})` : ''}`;
    }
    return `the “${t.template_name || t.template_key}” template (${t.template_key})`;
  }
  const subj = String(t.content?.subject || '').trim();
  return `${t.content_mode === 'design' ? 'a design e-mail' : 'a custom e-mail'}${subj ? `, subject “${subj}”` : ''}`;
};

/** The confirm's lines for moving `t` to `next`. */
export const switchLines = (
  t: Trigger, next: { enabled: boolean; mode: 'manual' | 'auto' }, ov: Overview | null,
): { title: string; lines: React.ReactNode[]; confirm: string; ack?: string; danger?: boolean } => {
  const s = ov?.settings;
  const nm = <b>{t.id} · {t.name}</b>;
  if (next.enabled && next.mode === 'auto') {
    // This trigger is about to be On + Auto, so "no trigger is auto" no longer applies.
    const closed = closedGates(ov, 1);
    const cadence = s ? `every ${s.interval_minutes ?? '?'} minutes, ${windowText(s)}` : 'on every scheduler run';
    const who = t.would_match === null
      ? 'Who would get it has not been counted — run a Dry run first to see the people and the reasons.'
      : `${num(t.would_match)} ${t.would_match === 1 ? 'person' : 'people'} would get it if it ran now (last dry run). New people are added as they start to match.`;
    const statusCap = s?.status_cap_30d ?? 6;
    return {
      title: t.enabled ? `Set ${t.id} to Auto — automatic sending?` : `Switch on ${t.id} — automatic sending?`,
      confirm: t.enabled ? 'Set to Auto and send automatically' : 'Switch on and send automatically',
      danger: true,
      ack: 'I understand members will get this e-mail without anyone pressing Send.',
      lines: [
        // Unknown (the status was not read) is said as such, in amber — never "nothing is sent yet".
        closed === null
          ? <span className="text-amber-800"><b>{STATE_UNKNOWN}</b>, so whether the server gate and the global switch are open cannot be told. If they are, from the next MEMAC run ({cadence}) {nm} e-mails <b>every member who matches it</b>, with no one pressing Send.</span>
          : closed.length === 0
          ? <>From the next MEMAC run ({cadence}), {nm} e-mails <b>every member who matches it</b>, with no one pressing Send.</>
          : <>Nothing is sent yet, because {closed.join(' and ')}. The moment {closed.length > 1 ? 'those are' : 'that is'} open, {nm} e-mails <b>every member who matches it</b> on every run ({cadence}), with no one pressing Send.</>,
        <>The e-mail: {contentText(t)}.</>,
        <>{who}</>,
        <>Per person: {capText(t)}.{' '}
          {t.is_service ? 'This is a service mail, so the status cap does not apply.'
            : `It also counts toward the cap of ${statusCap} non-service MEMAC or campaign e-mails per person in 30 days.`}
          {s?.max_per_run ? ` At most ${num(s.max_per_run)} e-mails per run across all triggers.` : ''}</>,
        <>Left out every time: our team, bounced or blocked addresses, inactive or deleting accounts
          {t.is_service ? '' : ', members who unsubscribed from this category with a MEMAC link'}
          {t.is_service || s?.exclude_unsubscribed === false ? '' : ', and SendGrid unsubscribes'}.</>,
        <>To stop it: switch the trigger Off here, or the global switch in Settings — it takes effect on the next run.</>,
      ],
    };
  }
  if (next.enabled) {
    return {
      title: `Switch on ${t.id} (manual)?`,
      confirm: 'Switch on (manual)',
      lines: [
        <>Nothing is sent automatically — the mode stays <b>Manual</b>.</>,
        <>{nm} is marked On. Members get it only when someone presses <b>Send to these N now</b> on this trigger and types the count.</>,
        <>Choosing Auto later asks again, with what it would send.</>,
      ],
    };
  }
  return {
    title: `Set ${t.id} to Auto?`,
    confirm: 'Set to Auto (stays off)',
    lines: [
      <>Nothing starts now: {nm} stays <b>Off</b>.</>,
      <>Once someone switches it On (that asks again), it will e-mail every matching member on every run, with no one pressing Send.</>,
    ],
  };
};

export const TriggerSwitch: React.FC<{
  t: Trigger; ov: Overview | null; onChanged: (row?: any) => void; onError?: (e: string) => void;
  /** Why it cannot be switched on right now (e.g. unsaved edits); off still works. */
  blockOn?: string;
  labelled?: boolean;
}> = ({ t, ov, onChanged, onError, blockOn, labelled }) => {
  const [pending, setPending] = useState<{ enabled: boolean; mode: 'manual' | 'auto' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const readOnly = t.read_only || t.external;
  const canOn = !readOnly && t.can_enable && !t.on_hold && !blockOn;
  // Auto is blocked by unsaved edits too: what would be armed is the saved version.
  const canAuto = !readOnly && t.can_enable && !t.on_hold && !t.manual_only && !blockOn;

  const post = async (next: { enabled: boolean; mode: 'manual' | 'auto' }) => {
    setBusy(true); setErr('');
    const { data, error } = await panelPost(SECTION, 'memac_trigger_toggle', {
      trigger_id: t.id, enabled: next.enabled, mode: next.mode, confirm: true,
    });
    setBusy(false);
    if (error) { setErr(error); onError?.(error); return false; }
    setPending(null);
    onChanged(data?.trigger);
    return true;
  };
  const ask = (next: { enabled: boolean; mode: 'manual' | 'auto' }) => {
    setErr('');
    // Only ever stops mail: no dialog.
    if (!next.enabled && next.mode === 'manual') { void post(next); return; }
    if (!next.enabled && t.enabled && next.mode === t.mode) { void post(next); return; }
    if (next.enabled === t.enabled && next.mode === 'manual') { void post(next); return; }
    setPending(next);
  };

  const why = t.external ? `Live elsewhere — switch it in ${t.ext?.where || 'the code that sends it'}`
    : t.alias_of ? `The same mail as ${t.alias_of} — switch that one`
      : t.on_hold ? `On hold${t.hold_reason ? ` — ${t.hold_reason}` : ' — cannot be switched on'}`
        : !t.can_enable ? 'Cannot be switched on' : blockOn || '';
  const box = (
    <input type="checkbox" checked={t.enabled} disabled={busy || readOnly || (!t.enabled && !canOn)}
      onChange={e => ask({ enabled: e.target.checked, mode: t.mode })}
      title={why || (t.enabled ? 'On — click to switch off' : 'Off — click to switch on (asks first)')}
      className="w-4 h-4 accent-[#9A7A4C]" aria-label={`${t.id} on`} />
  );
  const sel = (
    <select value={t.mode} disabled={busy || readOnly || t.on_hold || (!canAuto && t.mode !== 'auto')}
      onChange={e => ask({ enabled: t.enabled, mode: e.target.value === 'auto' ? 'auto' : 'manual' })}
      title={t.manual_only ? 'Broadcasts are sent by hand only'
        : blockOn ? blockOn : 'Manual: only Send to these N. Auto: the scheduler sends.'}
      className="h-8 px-1.5 rounded-md border-[0.5px] border-primary-300 bg-white text-xs" aria-label={`${t.id} mode`}>
      <option value="manual">Manual</option>
      <option value="auto" disabled={!canAuto}>Auto</option>
    </select>
  );
  const c = pending ? switchLines(t, pending, ov) : null;
  return (
    <>
      {labelled ? (
        <div className="flex items-center gap-4 flex-wrap text-sm text-grayscale-1200">
          <label className="flex items-center gap-2" title={why}>{box} On</label>
          <label className="flex items-center gap-2">Mode {sel}</label>
          {busy && <span className="text-xs text-grayscale-1100">Saving…</span>}
          {why && <span className="text-[11px] text-grayscale-1100">{why}</span>}
        </div>
      ) : (
        <span className="inline-flex items-center gap-2">{box}{sel}</span>
      )}
      {err && !pending && <div className="text-[11px] text-red-700">{err}</div>}
      {c && pending && (
        <ConfirmDialog title={c.title} lines={c.lines} confirmLabel={c.confirm} danger={c.danger} ack={c.ack}
          busy={busy} error={err} onClose={() => { setPending(null); setErr(''); }}
          onConfirm={() => { void post(pending); }} />
      )}
    </>
  );
};
